/*
  You can NOT currently use any component requiring REDUX STORE here because the PAGE is what contains the REDUX STORE
  So REDUX STORE is one CHILD below. Page will complain.
  What you need to do is use => `const { initialProps, initialState, store: ReduxStore } = pageProps`
  - pageProps ( contains Page information )
    - isServer: boolean
    - initialProps: Page return values ( dataLayer, metaData, etc...)
    - initialState: Flat Redux Store Object
    - store: Redux Store ( dispatch, subscribe, getState, etc... )
*/

import React from 'react'
import App from 'next/app'
// import { onLCP, onFID, onCLS, onINP, onFCP, onTTFB } from 'web-vitals/attribution' // Removing `webVitalsAttribution` Feature in next.config.json
import { onLCP, onFID, onCLS, onINP, onFCP, onTTFB } from 'next/dist/compiled/web-vitals'
import MetaData from 'components/MetaData'
import { getGeoLocationMetaData } from 'selectors/user/cookie' // TODO - Move to Locale
import { getAvailableCountries } from 'selectors/shared/locale'
import { getPagePath } from 'selectors/page'
import parse from 'lib/url/parse'
import 'sass/global.scss'
import useClearLocalStorage from 'hooks/clearLocalStorage'
import HoneyPotForm from 'components/Honeypot/HoneyPotForm'
import HoneyPotLink from 'components/Honeypot/honeypotLink'
import { getIsBotTraffic } from 'lib/bots/helpers'
import { PageInitialProps } from 'types/metadata'

const { APP_ENV } = process.env
const isDev = APP_ENV === 'local'
type Props = Record<string, any>

function reportMetric(metric: Record<string, any>) {
  if (isDev) {
    // The metric object ({ id, name, startTime, value, label }) is logged to the console
    // console.log('Web Vitals', { metric })
    return
  }

  /*
    In addition to the core metrics listed above, there are some additional custom metrics that measure the time it takes for the page to hydrate and render:
    Next.js-hydration: Length of time it takes for the page to start and finish hydrating (in ms)
    Next.js-route-change-to-render: Length of time it takes for a page to start rendering after a route change (in ms)
    Next.js-render: Length of time it takes for a page to finish render after a route change (in ms)
  */
  if (typeof window !== 'undefined') {
    const { id, name, value, attribution } = metric
    window.dataLayer.push({
      event: `web vitals ${name}`,
      // Google Analytics metrics must be integers, so the value is rounded.
      // For CLS the value is first multiplied by 1000 for greater precision
      // (note: increase the multiplier for greater precision if needed).
      vitals: Math.round(name === 'CLS' ? value * 1000 : value),
      // values must be integers
      // The `id` value will be unique to the current page load. When sending
      // multiple values from the same page (e.g. for CLS), Google Analytics can
      // compute a total by grouping on this ID (note: requires `eventLabel` to
      // be a dimension in your report).
      id,
      // id unique to current page load
      // CLS
      attributionElement:
        attribution?.largestShiftTarget ?? // LCP
        attribution?.element ??
        '',
    })
  }
}

// Track custom metrics
export function reportWebVitals(metric: Record<string, any>) {
  if (metric.label === 'custom') {
    // Only track custom metrics. For Web Vitals, we use the `web-vitals` package directly,
    // as it’s newer than what ships with Next.js 12.3.
    reportMetric(metric)
  }
}

// Track Web Vitals. This could be removed once we upgrade to Next.js 13
// (which includes https://github.com/vercel/next.js/pull/39368)
if (typeof window !== 'undefined') {
  onLCP(reportMetric)
  onFID(reportMetric)
  onCLS(reportMetric)
  onINP(reportMetric)
  onFCP(reportMetric)
  onTTFB(reportMetric)
}

export default class MyApp extends App<Props> {
  componentDidMount(): void {
    useClearLocalStorage()

    if (typeof window !== 'undefined') {
      const userAgent = navigator.userAgent

      if (getIsBotTraffic(userAgent)) {
        window.dataLayer.push({
          event: 'traffic_check',
          traffic_type: 'bot',
        })
      }
    }
  }

  getPageTitle = (pageTitle: string) => {
    const { pageProps } = this.props
    const { initialState } = pageProps
    const geoLocationMetaData = getGeoLocationMetaData(initialState)
    const countries = getAvailableCountries(initialState)
    const { storeLocale: currentStoreLocale } = geoLocationMetaData

    const currentIso2CountryCode = (currentStoreLocale.split('-')?.[1] || 'us').toUpperCase()

    if (currentIso2CountryCode === 'US') {
      return pageTitle
    }

    const currentCountryName = countries[currentIso2CountryCode] || ''

    return currentCountryName ? `${pageTitle} ${currentCountryName}` : pageTitle
  }

  render() {
    const { Component, pageProps, err } = this.props
    const { initialProps, initialState } = pageProps
    const { metaData } = (initialProps || {}) as PageInitialProps
    const { og, title } = metaData || {}

    const urlPath = this.props.router?.asPath || getPagePath(initialState)
    const { pathname: path, query } = parse(urlPath)

    let cleanPath = path
    if (cleanPath.match(/.*[/]$/)) {
      cleanPath = cleanPath.replace(/\/$/, '')
    }

    let pageTitle: string | undefined = title ? this.getPageTitle(title) : undefined
    let ogPageTitle: string | undefined = og?.title ? this.getPageTitle(og.title) : undefined

    if (query.page) {
      if (pageTitle) pageTitle = pageTitle.concat(` | Page ${query.page}`)
      if (ogPageTitle) ogPageTitle = ogPageTitle.concat(` | Page ${query.page}`)
    }

    return (
      <>
        <MetaData
          pageMetaData={metaData}
          pageTitle={pageTitle}
          ogPageTitle={ogPageTitle}
        />
        {cleanPath !== '/facsimile-npc' && (
          <>
            <HoneyPotForm />
            <HoneyPotLink />
          </>
        )}
        <Component {...{ ...pageProps, err }} />
      </>
    )
  }
}
